import React from 'react'
import { useDispatch } from 'react-redux'
import { useFirebase } from 'react-redux-firebase'
import { useNavigate } from 'react-router-dom'
import { ADMIN_EMAIL } from '../../../config/getEnvVars'
import { onIsLoading } from '../../../utils/onLoading'
import { actions as appActions, UserInfo } from '../reducer'
import {
  getFirebaseUserEmail,
  isAdminFirebaseUser,
} from '../auth'
import {
  ADMIN_SUBSCRIPTIONS_ROUTE,
  ADMIN_ROUTE,
  LOGIN_ROUTE,
  PORTAL_ROUTE,
  REGISTER_ROUTE,
} from '../../../config'
import { clientLogger } from '../../../utils/observability'

/** Map Firebase User to app UserInfo. */
function mapFirebaseUserToUserInfo(user: {
  uid: string
  email?: string | null
  displayName?: string | null
  photoURL?: string | null
  phoneNumber?: string | null
  providerData?: ReadonlyArray<{ email?: string | null } | null>
  providerId?: string
}): UserInfo {
  return {
    uid: user.uid,
    email: getFirebaseUserEmail(user),
    displayName: user.displayName ?? null,
    photoURL: user.photoURL ?? null,
    phoneNumber: user.phoneNumber ?? null,
    providerId: user.providerId ?? 'firebase',
  }
}

export const useAuthStateChange = () => {
  const timeoutRef = React.useRef<number>(0)
  const dispatch = useDispatch()
  const firebase = useFirebase()
  const navigate = useNavigate()

  React.useEffect(() => {
    window.navigate = navigate
  }, [navigate])

  React.useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((firebaseUser) => {
      const currentRoute = window.location.pathname
      const isRegister = currentRoute === REGISTER_ROUTE

      if (firebaseUser == null) {
        clientLogger.setUser(null)
        clientLogger.info('auth.signed_out', {
          route: currentRoute,
        })
        dispatch(appActions.onUserInfo(null))
        dispatch(appActions.onCustomerProfileId(undefined))
        dispatch(appActions.onSubscriptionIds(undefined))
        onIsLoading(false)
        return
      }

      const userInfo = mapFirebaseUserToUserInfo(firebaseUser)
      clientLogger.setUser(userInfo)
      dispatch(appActions.onUserInfo(userInfo))
      dispatch(appActions.onCustomerProfileId(undefined))
      dispatch(appActions.onSubscriptionIds(undefined))
      onIsLoading(false)

      if (isRegister) return

      const isAdmin = isAdminFirebaseUser(firebaseUser, ADMIN_EMAIL)
      clientLogger.info('auth.signed_in', {
        uid: userInfo.uid,
        route: currentRoute,
        isAdmin,
        providerId: userInfo.providerId,
      })

      const isLogin = currentRoute === LOGIN_ROUTE
      const isPortal = currentRoute === PORTAL_ROUTE
      const isAdminRoute =
        currentRoute === ADMIN_ROUTE ||
        currentRoute.startsWith(`${ADMIN_ROUTE}/`)

      timeoutRef.current = window.setTimeout(() => {
        if ((isLogin || isPortal) && isAdmin) {
          clientLogger.action('auth.redirect_admin', {
            from: currentRoute,
            to: ADMIN_SUBSCRIPTIONS_ROUTE,
            uid: userInfo.uid,
          })
          navigate(ADMIN_SUBSCRIPTIONS_ROUTE)
        } else if ((isLogin || isAdminRoute) && !isAdmin) {
          clientLogger.action('auth.redirect_portal', {
            from: currentRoute,
            to: PORTAL_ROUTE,
            uid: userInfo.uid,
          })
          navigate(PORTAL_ROUTE)
        }
      }, 1000)
    })

    return () => {
      unsubscribe()
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
    }
  }, [firebase, dispatch, navigate])
}
